import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'lib/utils';

const typographyVariants = cva('', {
  variants: {
    variant: {
      // Hero scale — FK Grotesk Medium, reserved for landing/hero moments.
      // Sizes lifted from rye.com computed styles.
      display: 'font-display font-medium text-[72px] leading-none tracking-[-0.02em]',
      h1: 'font-display font-medium text-[60px] leading-none tracking-[-0.02em]',
      // App scale — Inter semibold. h2 used to ship as 36px FK Grotesk Medium
      // but the display family at weight 500 reads as too thin and too tall
      // against the rest of the UI; Inter at 26/600 carries the same hierarchy
      // signal without overwhelming.
      h2: 'font-sans font-semibold text-[26px] leading-[1.2] tracking-[-0.015em]',
      h3: 'font-sans font-semibold text-[18px] leading-[1.3] tracking-[-0.005em]',
      h4: 'font-sans font-semibold text-[14px] leading-[1.3]',
      // Mono uppercase — eyebrows above titles, form labels.
      eyebrow:
        'font-mono font-medium text-[11px] uppercase tracking-[0.14em] text-[var(--text-muted)]',
      label:
        'font-mono font-medium text-[11px] uppercase tracking-[0.14em] text-[var(--text-muted)]',
      // Body scale.
      lede: 'text-base leading-[1.55] text-[var(--text-muted)] max-w-[60ch]',
      body: 'text-[15px] leading-[1.55]',
      small: 'text-[13px] leading-5 text-[var(--text-muted)]',
      // FK Grotesk Mono — ligatures off so IDs / hashes render true-mono.
      mono: "font-mono [font-feature-settings:'liga'_0,'calt'_0]",
      // Inline link — bumps weight + ink color above the surrounding paragraph,
      // 2px line-strong underline that warms to accent on hover. Pair with
      // `asChild` on a router/anchor element.
      link: 'font-medium text-[var(--text)] underline underline-offset-4 decoration-[var(--line-strong)] decoration-2 transition-colors hover:decoration-[var(--accent)]',
    },
  },
  defaultVariants: {
    variant: 'body',
  },
});

type TypographyVariant = NonNullable<VariantProps<typeof typographyVariants>['variant']>;

const variantToTag: Record<TypographyVariant, React.ElementType> = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  eyebrow: 'span',
  label: 'span',
  lede: 'p',
  body: 'p',
  small: 'span',
  mono: 'span',
  link: 'a',
};

export interface TypographyProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'color'>,
    VariantProps<typeof typographyVariants> {
  asChild?: boolean;
  as?: React.ElementType;
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  ({ className, variant, as, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : as ?? variantToTag[variant ?? 'body'];
    return <Comp ref={ref} className={cn(typographyVariants({ variant }), className)} {...props} />;
  },
);
Typography.displayName = 'Typography';

export { Typography, typographyVariants };
